import React, { useState } from "react";
import client from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Facebook, Linkedin, Youtube, Instagram, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = [
  "Portugal", "Brasil", "Angola", "Moçambique", "Cabo Verde", "São Tomé e Príncipe",
  "Espanha", "França", "Reino Unido", "Alemanha", "Suíça", "Bélgica", "Holanda",
  "Itália", "Estados Unidos", "Canadá", "Outro",
];

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-[#A29F9B]">
      {label}{required && <span className="text-[#C5A880]">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls = "w-full bg-[#0C0E12] border border-white/10 rounded-xl px-4 py-3 text-[#F5F2EE] text-sm outline-none focus:border-[#C5A880]/50 transition-colors";

const Contact = () => {
  const [form, setForm] = useState({
    subject: "", title: "", name: "", organization: "",
    country: "Portugal", locality: "", phone: "", email: "", message: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Por favor aceita a Política de Proteção de Dados.");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Escreve um pouco mais sobre o teu pedido.");
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Indica o assunto da tua mensagem.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/contact", {
        subject: form.subject.trim(),
        title: form.title || null,
        name: form.name,
        organization: form.organization || null,
        country: form.country || null,
        locality: form.locality || null,
        phone: form.phone || null,
        email: form.email,
        message: form.message,
      });
      setDone(true);
      toast.success("Mensagem enviada. Vamos responder-te em breve.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar. Tenta novamente.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] flex flex-col" data-testid="contact-page">
      <SiteHeader />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 md:px-12 py-12">
        <span className="label-uppercase">Fala connosco</span>
        <h1 className="font-display text-4xl md:text-6xl mt-3 mb-4">
          Contactos <em className="text-[#C5A880]">Soulmatch</em>
        </h1>
        <p className="text-[#DCD7D2] max-w-2xl mb-12 leading-relaxed">
          Estamos aqui para te ajudar — coaching, parcerias, dúvidas sobre a app, eventos.
          Escolhe o canal que preferires.
        </p>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left column — coordinates */}
          <aside className="lg:col-span-2 space-y-8" data-testid="contact-info">
            <section className="bg-[#161920] border border-white/5 rounded-2xl p-6">
              <h2 className="font-display text-2xl mb-1">SOLFUT, Lda</h2>
              <p className="text-sm text-[#A29F9B] mb-6">I Have the Power · Soulmatch</p>

              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#C5A880] mt-0.5 shrink-0" />
                  <div>
                    <a href="tel:00351224053270" className="text-[#F5F2EE] hover:text-[#C5A880] block" data-testid="contact-phone">
                      (+351) 224 053 270
                    </a>
                    <span className="text-xs text-[#A29F9B]">Custo de chamada para a rede fixa nacional.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#C5A880] mt-0.5 shrink-0" />
                  <a href="mailto:soulmatch@solfut.com" className="text-[#F5F2EE] hover:text-[#C5A880]" data-testid="contact-email">
                    soulmatch@solfut.com
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle className="w-4 h-4 text-[#C5A880] mt-0.5 shrink-0" />
                  <div>
                    <a href="https://api.whatsapp.com/send?phone=351926948606" target="_blank" rel="noopener noreferrer"
                      className="text-[#F5F2EE] hover:text-[#C5A880] block" data-testid="contact-whatsapp">
                      WhatsApp: (+351) 926 948 606
                    </a>
                    <span className="text-xs text-[#A29F9B]">Envia mensagem directa.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#C5A880] mt-0.5 shrink-0" />
                  <div className="text-[#DCD7D2]">
                    <div className="text-xs uppercase tracking-wider text-[#A29F9B] mb-1">Horário</div>
                    <div>Dias úteis: 9h30 – 12h30 · 14h00 – 19h00</div>
                  </div>
                </li>
              </ul>

              <div className="mt-6 pt-6 border-t border-white/5 flex flex-wrap gap-3" data-testid="contact-socials">
                <a href="https://www.facebook.com/IHTPGLOBAL" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/10 hover:border-[#C5A880] hover:text-[#C5A880] text-[#DCD7D2] inline-flex items-center justify-center transition-colors" aria-label="Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://www.linkedin.com/company/solfut-lda---i-have-the-power/" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/10 hover:border-[#C5A880] hover:text-[#C5A880] text-[#DCD7D2] inline-flex items-center justify-center transition-colors" aria-label="LinkedIn">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="https://www.youtube.com/user/solfut" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/10 hover:border-[#C5A880] hover:text-[#C5A880] text-[#DCD7D2] inline-flex items-center justify-center transition-colors" aria-label="YouTube">
                  <Youtube className="w-4 h-4" />
                </a>
                <a href="https://www.instagram.com/ihavethepowerglobal/" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/10 hover:border-[#C5A880] hover:text-[#C5A880] text-[#DCD7D2] inline-flex items-center justify-center transition-colors" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </section>

            <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 text-sm text-[#DCD7D2] space-y-4" data-testid="contact-addresses">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#C5A880]" />
                  <span className="label-uppercase">Excellence Center</span>
                </div>
                <div>Edifício Camões</div>
                <div>Rua de Camões nº 117 — 6º andar</div>
                <div>4000-144 Porto · Portugal</div>
              </div>
              <div className="pt-4 border-t border-white/5 text-xs text-[#A29F9B]">
                NIF 503 928 518 — Solfut, Consultoria e Formação, Lda.
              </div>
            </section>
          </aside>

          {/* Right column — form */}
          <section className="lg:col-span-3 bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="contact-form-card">
            {done ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-[#C5A880] mx-auto mb-4" />
                <h2 className="font-display text-3xl mb-3">Mensagem recebida</h2>
                <p className="text-[#DCD7D2] max-w-md mx-auto">
                  Obrigado, {form.name}. Vamos responder-te ao <strong className="text-[#C5A880]">{form.email}</strong> dentro de 1 dia útil.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <h2 className="font-display text-2xl mb-2">Pedir informação</h2>

                <Field label="Assunto" required>
                  <input type="text" value={form.subject} onChange={(e)=>update("subject", e.target.value)}
                    className={inputCls} placeholder="Em poucas palavras, qual é o teu pedido?" required maxLength={120}
                    data-testid="contact-subject" />
                </Field>

                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Título">
                    <select value={form.title} onChange={(e)=>update("title", e.target.value)}
                      className={inputCls} data-testid="contact-title">
                      <option value="">—</option>
                      <option value="Sr.">Sr.</option>
                      <option value="Sra.">Sra.</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Nome" required>
                      <input type="text" value={form.name} onChange={(e)=>update("name", e.target.value)}
                        className={inputCls} required data-testid="contact-name" />
                    </Field>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Organização">
                    <input type="text" value={form.organization} onChange={(e)=>update("organization", e.target.value)}
                      className={inputCls} data-testid="contact-organization" />
                  </Field>
                  <Field label="País">
                    <select value={form.country} onChange={(e)=>update("country", e.target.value)}
                      className={inputCls} data-testid="contact-country">
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Localidade" required>
                    <input type="text" value={form.locality} onChange={(e)=>update("locality", e.target.value)}
                      className={inputCls} required data-testid="contact-locality" />
                  </Field>
                  <Field label="Telefone">
                    <input type="tel" value={form.phone} onChange={(e)=>update("phone", e.target.value)}
                      className={inputCls} data-testid="contact-phone-input" />
                  </Field>
                </div>

                <Field label="Email" required>
                  <input type="email" value={form.email} onChange={(e)=>update("email", e.target.value)}
                    className={inputCls} required data-testid="contact-email-input" />
                </Field>

                <Field label="Que informação gostarias de obter?" required>
                  <textarea rows={5} value={form.message} onChange={(e)=>update("message", e.target.value)}
                    className={inputCls} required minLength={10} data-testid="contact-message" />
                </Field>

                <label className="flex items-start gap-3 text-sm text-[#DCD7D2]">
                  <input type="checkbox" checked={form.consent} onChange={(e)=>update("consent", e.target.checked)}
                    className="mt-1 accent-[#C5A880] w-4 h-4" data-testid="contact-consent" />
                  <span>
                    Li e aceito a <a href="/privacidade" className="text-[#C5A880] hover:underline">Política de Proteção de Dados</a>.
                    Os meus dados serão usados apenas para responder a este pedido.
                  </span>
                </label>

                <button type="submit" disabled={busy || !form.consent}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "A enviar..." : "Enviar mensagem"}
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
